"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  Flame,
  Trophy,
  Mic,
  BarChart3,
  Zap,
  CheckCircle2,
  Lightbulb,
  Star,
  Check,
  Crown,
  Medal,
  Target,
  Timer,
  Shuffle,
  Gamepad2,
  Swords,
  Bot,
  Rocket,
  Award,
  RotateCcw,
  Clock,
} from "lucide-react";

/* ── Constants ── */
const SCENE_DURATION = 4000;
const SCENE_COUNT = 6;

const SCENE_URLS = [
  "app.closer-ai.nl/dashboard",
  "app.closer-ai.nl/trainer",
  "app.closer-ai.nl/session/live",
  "app.closer-ai.nl/session/resultaat",
  "app.closer-ai.nl/leaderboard",
  "app.closer-ai.nl/insights",
];

/* ── Shared animation variants ── */
const sceneVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const sceneTransition = { duration: 0.4, ease: "easeOut" as const };

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.12 } },
};

const staggerItem = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

/* ── Animated score counter ── */
function AnimatedScore({ target }: { target: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 800;
    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }, [target]);

  return <>{count}</>;
}

/* ── Sparkline (mini trend chart) ── */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const h = 24;
  const w = 60;
  const points = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`)
    .join(" ");

  return (
    <motion.svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="overflow-visible"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );
}

/* ── Radar Chart (pentagon SVG) ── */
function RadarChart({ skills }: { skills: { label: string; value: number }[] }) {
  const cx = 50;
  const cy = 50;
  const r = 38;
  const count = skills.length;

  const getPoint = (index: number, value: number) => {
    const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
    const dist = (value / 100) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  };

  const gridPoints = (scale: number) =>
    Array.from({ length: count })
      .map((_, i) => {
        const p = getPoint(i, scale * 100);
        return `${p.x},${p.y}`;
      })
      .join(" ");

  const dataPoints = skills.map((s, i) => getPoint(i, s.value));
  const dataPath = dataPoints.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      {/* Grid rings */}
      {[0.25, 0.5, 0.75, 1].map((scale) => (
        <polygon
          key={scale}
          points={gridPoints(scale)}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="0.5"
        />
      ))}
      {/* Axis lines */}
      {skills.map((_, i) => {
        const p = getPoint(i, 100);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="0.5"
          />
        );
      })}
      {/* Data polygon */}
      <motion.polygon
        points={dataPath}
        fill="rgba(59,130,246,0.15)"
        stroke="#3b82f6"
        strokeWidth="1.5"
        strokeLinejoin="round"
        initial={{ opacity: 0, scale: 0.3 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        style={{ transformOrigin: "50% 50%" }}
      />
      {/* Data points */}
      {dataPoints.map((p, i) => (
        <motion.circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="2"
          fill="#3b82f6"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 + i * 0.1 }}
        />
      ))}
      {/* Labels */}
      {skills.map((s, i) => {
        const p = getPoint(i, 120);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-gray-500"
            fontSize="5"
          >
            {s.label}
          </text>
        );
      })}
    </svg>
  );
}

/* ── Donut Chart ── */
function DonutChart({ segments }: { segments: { label: string; value: number; color: string }[] }) {
  const size = 80;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const cx = size / 2;
  const cy = size / 2;

  let cumulativeOffset = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full">
      {/* Background ring */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.05)"
        strokeWidth={strokeWidth}
      />
      {/* Segments */}
      {segments.map((seg, i) => {
        const segLength = (seg.value / 100) * circumference;
        const offset = cumulativeOffset;
        cumulativeOffset += segLength;

        return (
          <motion.circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${segLength} ${circumference - segLength}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 + i * 0.2 }}
          />
        );
      })}
      {/* Center text */}
      <text
        x={cx}
        y={cy - 3}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-white font-bold"
        fontSize="12"
      >
        62%
      </text>
      <text
        x={cx}
        y={cy + 7}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-gray-500"
        fontSize="5"
      >
        spreektijd
      </text>
    </svg>
  );
}

/* ── Scene 0: Dashboard ── */
const radarSkills = [
  { label: "Opening", value: 92 },
  { label: "Bezwaren", value: 78 },
  { label: "Closing", value: 65 },
  { label: "Empathie", value: 88 },
  { label: "Product", value: 71 },
];

function DashboardScene() {
  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="p-4">
      {/* Welcome banner with XP */}
      <motion.div
        variants={staggerItem}
        className="mb-3 rounded-lg border border-blue-500/10 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 p-3"
      >
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-white">Welkom terug, Lisa</div>
            <div className="text-[11px] text-gray-400">Level 12 — 2.840 / 3.200 XP</div>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-400">
            <Flame className="h-3 w-3" /> 7 dagen
          </div>
        </div>
        {/* XP bar */}
        <div className="h-1.5 rounded-full bg-white/5">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
            style={{ transformOrigin: "left" }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 2840 / 3200 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          />
        </div>
      </motion.div>

      {/* Stats row */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] text-gray-500">Gem. Score</div>
              <div className="text-sm font-bold text-white">84</div>
              <div className="text-[11px] text-emerald-400">+6 vs vorige week</div>
            </div>
            <Sparkline data={[72, 75, 78, 80, 76, 82, 84]} color="#3b82f6" />
          </div>
        </motion.div>
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5"
        >
          <div className="flex items-center gap-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/20">
              <Mic className="h-3 w-3 text-white" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500">Sessies</div>
              <div className="text-sm font-bold text-white">5</div>
              <div className="text-[11px] text-emerald-400">+2</div>
            </div>
          </div>
        </motion.div>
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5"
        >
          <div className="flex items-center gap-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-500/20">
              <Clock className="h-3 w-3 text-white" />
            </div>
            <div>
              <div className="text-[11px] text-gray-500">Tijd</div>
              <div className="text-sm font-bold text-white">48m</div>
              <div className="text-[11px] text-emerald-400">+12m</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Two columns: Radar + Challenge */}
      <div className="grid grid-cols-2 gap-2">
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-white/5 bg-white/[0.02] p-2"
        >
          <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-gray-400">
            <BarChart3 className="h-2.5 w-2.5 text-blue-400" /> Skills
          </div>
          <div className="h-[100px]">
            <RadarChart skills={radarSkills} />
          </div>
        </motion.div>

        <div className="space-y-2">
          <motion.div
            variants={staggerItem}
            className="rounded-lg border border-purple-500/10 bg-purple-500/5 p-2.5"
          >
            <div className="mb-1.5 flex items-center gap-1.5">
              <Target className="h-3 w-3 text-purple-400" />
              <span className="text-[11px] font-semibold text-purple-300">
                Dagelijkse Challenge
              </span>
            </div>
            <div className="mb-2 text-[11px] text-gray-300">
              Sluit 3 gesprekken af met score 80+
            </div>
            <div className="flex gap-0.5">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full ${i <= 2 ? "bg-purple-500" : "bg-white/10"}`}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={staggerItem}
            className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5"
          >
            <div className="mb-1 text-[11px] text-gray-500">Aanbevolen</div>
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-amber-500/10">
                <Zap className="h-2.5 w-2.5 text-amber-400" />
              </div>
              <div>
                <div className="text-[11px] font-medium text-white">Bezwaar — Budget</div>
                <div className="text-[11px] text-gray-500">Moeilijk</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Scene 1: AI Trainer ── */
function TrainerScene() {
  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="p-4">
      {/* Header */}
      <motion.div variants={staggerItem} className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/20">
            <Bot className="h-4 w-4 text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white">AI Sales Trainer</div>
            <div className="text-[11px] text-gray-500">Kies je training</div>
          </div>
        </div>
      </motion.div>

      {/* Quick start cards */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-blue-500/15 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 p-3"
        >
          <div className="mb-1.5 flex items-center gap-2">
            <Bot className="h-4 w-4 text-blue-400" />
            <span className="text-[11px] font-semibold text-white">Vrij Oefenen</span>
          </div>
          <div className="text-[11px] text-gray-400">Kies een scenario en start</div>
        </motion.div>
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-white/5 bg-white/[0.03] p-3"
        >
          <div className="mb-1.5 flex items-center gap-2">
            <Rocket className="h-4 w-4 text-emerald-400" />
            <span className="text-[11px] font-semibold text-white">Snelle Pitch</span>
          </div>
          <div className="text-[11px] text-gray-400">5 min challenge</div>
        </motion.div>
      </div>

      {/* Special modes */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-amber-500/10 bg-amber-500/5 p-2.5 text-center"
        >
          <Timer className="mx-auto mb-1 h-4 w-4 text-amber-400" />
          <div className="text-[11px] font-semibold text-white">Blitz</div>
          <div className="mt-0.5 inline-block rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[11px] font-medium text-amber-400">
            1.1x XP
          </div>
        </motion.div>
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-purple-500/10 bg-purple-500/5 p-2.5 text-center"
        >
          <Shuffle className="mx-auto mb-1 h-4 w-4 text-purple-400" />
          <div className="text-[11px] font-semibold text-white">Mystery</div>
          <div className="mt-0.5 text-[11px] text-gray-500">Willekeurig</div>
        </motion.div>
        <motion.div
          variants={staggerItem}
          className="rounded-lg border border-emerald-500/10 bg-emerald-500/5 p-2.5 text-center"
        >
          <Gamepad2 className="mx-auto mb-1 h-4 w-4 text-emerald-400" />
          <div className="text-[11px] font-semibold text-white">Mini-Games</div>
          <div className="mt-0.5 text-[11px] text-gray-500">Deelvaardigheden</div>
        </motion.div>
      </div>

      {/* Boss Battle teaser */}
      <motion.div
        variants={staggerItem}
        className="flex items-center justify-between rounded-lg border border-red-500/10 bg-gradient-to-r from-red-500/5 to-orange-500/5 px-3 py-2.5"
      >
        <div className="flex items-center gap-2">
          <Swords className="h-4 w-4 text-red-400" />
          <div>
            <div className="text-[11px] font-semibold text-red-300">Boss Battle</div>
            <div className="text-[11px] text-gray-500">De Onmogelijke Klant</div>
          </div>
        </div>
        <div className="rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-400">
          Expert
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Scene 2: Voice Session ── */
const WAVEFORM_BARS = [
  3, 5, 8, 12, 16, 20, 16, 22, 18, 14, 10, 16, 20, 14, 8, 12, 18, 22, 16, 10, 6, 4,
];

function ChatBubble({ text, isUser, delay }: { text: string; isUser?: boolean; delay: number }) {
  return (
    <motion.div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
    >
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-[11px] leading-relaxed ${
          isUser
            ? "border border-blue-500/20 bg-blue-500/15 text-blue-200"
            : "border border-white/5 bg-white/[0.04] text-gray-300"
        }`}
      >
        {text}
      </div>
    </motion.div>
  );
}

function VoiceSessionScene() {
  return (
    <div className="p-4">
      {/* Header */}
      <motion.div
        className="mb-4 flex items-center justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500/20">
            <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white">Cold Call — IT Manager</div>
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <span>Live sessie</span>
              <span className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-medium text-amber-400">
                Sceptisch
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-300">
          <Mic className="h-3 w-3 text-red-400" />
          02:34
        </div>
      </motion.div>

      {/* Waveform */}
      <motion.div
        className="mb-4 flex h-8 items-center justify-center gap-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        {WAVEFORM_BARS.map((h, i) => (
          <motion.div
            key={i}
            className="w-[3px] rounded-full bg-blue-500"
            style={{ transformOrigin: "bottom" }}
            animate={{
              scaleY: [1, h / 8, 1, h / 6, 1],
            }}
            transition={{
              duration: 1.2 + Math.random() * 0.6,
              repeat: Infinity,
              repeatType: "loop",
              delay: i * 0.04,
              ease: "easeInOut",
            }}
            initial={{ height: 4 }}
          />
        ))}
      </motion.div>

      {/* Chat bubbles */}
      <div className="space-y-2">
        <ChatBubble
          text="Klinkt interessant, maar we hebben al een oplossing voor salestraining..."
          delay={0.3}
        />
        <ChatBubble
          text="Begrijpelijk. Wat zou er moeten veranderen om het nóg effectiever te maken?"
          isUser
          delay={0.9}
        />
        <ChatBubble text="Nou, als ik eerlijk ben missen we real-time feedback..." delay={1.5} />
      </div>

      {/* Live coaching hint */}
      <motion.div
        className="mt-3 flex items-center gap-1.5 rounded-md border border-emerald-500/10 bg-emerald-500/5 px-3 py-1.5 text-[11px] text-emerald-400"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 2 }}
      >
        <Lightbulb className="h-3 w-3 flex-shrink-0" />
        Tip: Vraag door op &quot;real-time feedback&quot; — dit is jouw ingang
      </motion.div>
    </div>
  );
}

/* ── Scene 3: Results & Gamification ── */
const scoreBreakdown = [
  { label: "Opening", score: 92 },
  { label: "Bezwaren", score: 78 },
  { label: "Closing", score: 85 },
  { label: "Rapport", score: 88 },
];

function ResultsScene() {
  return (
    <div className="p-4">
      {/* Big score + XP */}
      <motion.div
        className="mb-3 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-1 text-[11px] text-gray-500">Sessiescore</div>
        <div className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-4xl font-extrabold text-transparent">
          <AnimatedScore target={86} />
          <span className="text-lg font-normal text-gray-500">/100</span>
        </div>
        <motion.div
          className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
          +120 XP verdiend
        </motion.div>
      </motion.div>

      {/* Gamification row */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <motion.div
          className="rounded-lg border border-orange-500/10 bg-gradient-to-br from-orange-500/10 to-red-500/10 p-2.5 text-center"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.7, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Flame className="mx-auto mb-0.5 h-4 w-4 text-orange-400" />
          <div className="text-sm font-extrabold text-orange-300">2.5x</div>
          <div className="text-[11px] text-gray-500">Combo</div>
        </motion.div>
        <motion.div
          className="rounded-lg border border-amber-500/10 bg-amber-500/5 p-2.5 text-center"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.85, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Zap className="mx-auto mb-0.5 h-4 w-4 text-amber-400" />
          <div className="text-sm font-extrabold text-amber-300">+45</div>
          <div className="text-[11px] text-gray-500">Bonus XP</div>
        </motion.div>
        <motion.div
          className="rounded-lg border border-purple-500/10 bg-purple-500/5 p-2.5 text-center"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Award className="mx-auto mb-0.5 h-4 w-4 text-purple-400" />
          <div className="text-[11px] font-bold text-purple-300">Closing</div>
          <div className="text-[11px] text-gray-500">Expert</div>
        </motion.div>
      </div>

      {/* Compact skill bars */}
      <div className="mb-3 space-y-1.5">
        {scoreBreakdown.map((s) => (
          <div key={s.label} className="space-y-0.5">
            <div className="flex justify-between text-[11px]">
              <span className="text-gray-300">{s.label}</span>
              <span className="text-gray-500">{s.score}%</span>
            </div>
            <div className="h-1 rounded-full bg-white/5">
              <motion.div
                className={`h-full rounded-full ${s.score >= 85 ? "bg-emerald-500" : s.score >= 70 ? "bg-blue-500" : "bg-amber-500"}`}
                style={{ transformOrigin: "left" }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: s.score / 100 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Replay button */}
      <motion.div
        className="flex items-center justify-center gap-1.5 rounded-lg border border-blue-500/20 bg-blue-500/5 py-2 text-[11px] font-medium text-blue-400"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <RotateCcw className="h-3 w-3" />
        Verbeter je score
      </motion.div>
    </div>
  );
}

/* ── Scene 4: Leaderboard ── */
const leaderboardData = [
  { rank: 1, name: "Sophie de Vries", xp: "4.280", sessions: 34, streak: 12 },
  { rank: 2, name: "Luuk van Dijk", xp: "3.910", sessions: 28, streak: 8 },
  { rank: 3, name: "Emma Jansen", xp: "3.650", sessions: 31, streak: 0 },
  { rank: 4, name: "Daan Bakker", xp: "2.890", sessions: 22, streak: 0 },
  { rank: 5, name: "Lisa Visser", xp: "2.450", sessions: 19, streak: 0 },
];

function LeaderboardScene() {
  return (
    <div className="p-4">
      {/* Header with tabs */}
      <motion.div
        className="mb-4 flex items-center justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/20">
            <Trophy className="h-4 w-4 text-amber-400" />
          </div>
          <div className="text-xs font-semibold text-white">Leaderboard</div>
        </div>
        {/* Tabs */}
        <div className="flex gap-1">
          <div className="rounded-full border border-blue-500/20 bg-blue-500/15 px-2.5 py-0.5 text-[11px] font-medium text-blue-400">
            Algemeen
          </div>
          <div className="rounded-full bg-white/5 px-2.5 py-0.5 text-[11px] text-gray-500">
            Blitz
          </div>
        </div>
      </motion.div>

      {/* Leaderboard rows */}
      <div className="space-y-1.5">
        {leaderboardData.map((entry, i) => (
          <motion.div
            key={entry.rank}
            className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 ${
              entry.rank === 1
                ? "border border-amber-500/15 bg-amber-500/5"
                : "border border-white/5 bg-white/[0.02]"
            }`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 + i * 0.12 }}
          >
            {/* Rank */}
            <div className="flex h-5 w-5 items-center justify-center">
              {entry.rank === 1 ? (
                <Crown className="h-4 w-4 text-amber-400" />
              ) : entry.rank <= 3 ? (
                <Medal className="h-3.5 w-3.5 text-gray-400" />
              ) : (
                <span className="text-[11px] font-semibold text-gray-500">{entry.rank}</span>
              )}
            </div>

            {/* Avatar */}
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/15 text-[11px] font-bold text-blue-300">
              {entry.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)}
            </div>

            {/* Name + sessions */}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-medium text-white">{entry.name}</div>
              <div className="text-[11px] text-gray-500">{entry.sessions} sessies</div>
            </div>

            {/* Streak */}
            {entry.streak > 0 && (
              <div className="flex items-center gap-0.5 text-[11px] text-amber-400">
                <Flame className="h-2.5 w-2.5" />
                {entry.streak}
              </div>
            )}

            {/* XP */}
            <div className="text-[11px] font-bold text-blue-400">{entry.xp} XP</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ── Scene 5: Insights / Speech Analytics ── */
const fillerWords = [
  { word: "uhm", count: 12 },
  { word: "eigenlijk", count: 8 },
  { word: "zeg maar", count: 5 },
  { word: "basically", count: 3 },
];

function InsightsScene() {
  const maxFiller = Math.max(...fillerWords.map((f) => f.count));

  return (
    <div className="p-4">
      {/* Header */}
      <motion.div
        className="mb-3 flex items-center justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/20">
            <BarChart3 className="h-4 w-4 text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white">Inzichten</div>
            <div className="text-[11px] text-gray-500">Jouw gesprekspatronen</div>
          </div>
        </div>
      </motion.div>

      {/* Mini stats row */}
      <div className="mb-3 grid grid-cols-4 gap-1.5">
        {[
          { label: "Sessies", value: "24" },
          { label: "WPM", value: "142" },
          { label: "Fillers", value: "18" },
          { label: "Talk ratio", value: "1.4x" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            className="rounded-md border border-white/5 bg-white/[0.03] px-2 py-1.5 text-center"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.08 }}
          >
            <div className="text-xs font-bold text-white">{stat.value}</div>
            <div className="text-[11px] text-gray-500">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Two columns: Donut + Filler words */}
      <div className="grid grid-cols-2 gap-2">
        {/* Donut chart */}
        <motion.div
          className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mb-1 text-[11px] font-semibold text-gray-400">Spreektijd</div>
          <div className="mx-auto h-[72px] w-[72px]">
            <DonutChart
              segments={[
                { label: "Jij", value: 62, color: "#3b82f6" },
                { label: "AI", value: 38, color: "#6366f1" },
              ]}
            />
          </div>
          <div className="mt-1.5 flex justify-center gap-3 text-[11px]">
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              <span className="text-gray-400">Jij 62%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
              <span className="text-gray-400">AI 38%</span>
            </div>
          </div>
        </motion.div>

        {/* Filler words */}
        <motion.div
          className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="mb-2 text-[11px] font-semibold text-gray-400">Stopwoorden</div>
          <div className="space-y-1.5">
            {fillerWords.map((f, i) => (
              <div key={f.word} className="flex items-center gap-2">
                <span className="w-[52px] truncate text-[11px] text-gray-400 italic">{f.word}</span>
                <div className="h-1 flex-1 rounded-full bg-white/5">
                  <motion.div
                    className="h-full rounded-full bg-indigo-500"
                    style={{ transformOrigin: "left" }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: f.count / maxFiller }}
                    transition={{ duration: 0.6, delay: 0.5 + i * 0.1 }}
                  />
                </div>
                <span className="w-[16px] text-right text-[11px] text-gray-500">{f.count}x</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Trend indicator */}
      <motion.div
        className="mt-2.5 flex items-center gap-2 rounded-md border border-emerald-500/10 bg-emerald-500/5 px-3 py-1.5 text-[11px] text-emerald-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <TrendingUp className="h-3 w-3 flex-shrink-0" />
        Spreektempo verbeterd met 8% in 4 weken
      </motion.div>
    </div>
  );
}

/* ── Main Component ── */
const scenes = [
  DashboardScene,
  TrainerScene,
  VoiceSessionScene,
  ResultsScene,
  LeaderboardScene,
  InsightsScene,
];

export default function HeroMockup() {
  const [sceneIndex, setSceneIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, active: false });

  const goToScene = useCallback((index: number) => {
    setSceneIndex(index);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSceneIndex((prev) => (prev + 1) % SCENE_COUNT);
    }, SCENE_DURATION);
    return () => clearInterval(interval);
  }, [sceneIndex]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setSpotlight({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
      active: true,
    });
  }, []);

  const CurrentScene = scenes[sceneIndex];

  return (
    <motion.div
      ref={containerRef}
      className="relative"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setSpotlight((s) => ({ ...s, active: false }))}
    >
      {/* Cursor spotlight */}
      <div
        className="pointer-events-none absolute -inset-8 rounded-2xl transition-opacity duration-500"
        style={{
          opacity: spotlight.active ? 1 : 0,
          background: `radial-gradient(400px circle at ${spotlight.x}% ${spotlight.y}%, rgba(59, 130, 246, 0.12), transparent 60%)`,
        }}
      />
      {/* Glow effect */}
      <div className="absolute -inset-4 rounded-2xl bg-blue-500/10 blur-2xl" />

      {/* Browser frame */}
      <div className="animated-gradient-border relative overflow-hidden rounded-xl border border-white/10 bg-[#0a0f1c] shadow-2xl shadow-blue-500/5">
        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-2.5">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
            <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
            <div className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="ml-3 flex-1 overflow-hidden rounded-md bg-white/5 px-3 py-1 text-[11px] text-gray-500">
            <AnimatePresence mode="wait">
              <motion.span
                key={sceneIndex}
                className="block"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
              >
                {SCENE_URLS[sceneIndex]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-[2px] bg-white/5">
          <motion.div
            key={sceneIndex}
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: SCENE_DURATION / 1000, ease: "linear" }}
          />
        </div>

        {/* Scene content */}
        <div className="min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={sceneIndex}
              variants={sceneVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={sceneTransition}
            >
              <CurrentScene />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation dots */}
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: SCENE_COUNT }).map((_, i) => (
          <button
            key={i}
            onClick={() => goToScene(i)}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
              i === sceneIndex ? "w-6 bg-blue-500" : "w-1.5 bg-white/20 hover:bg-white/40"
            }`}
            aria-label={`Scene ${i + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
}
